import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Save, X, CheckCircle, Clock, AlertTriangle, Send, Home, LogOut } from 'lucide-react';
import API_URL from '../../config';
import '../Home.css';
import './FacultyHome.css';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DEPTS = ["CSE", "CSE-AIML&IOT", "AIDS", "CSBS", "IT", "ECE", "EEE", "EIE", "MECH", "CIVIL", "BIOTECH", "DS", "CYS", "R&AI"];
const YEARS = ["1st year", "2nd year", "3rd year", "4th year"];
const SECTIONS = ["A", "B", "C", "D"];

// Period time slots per year group
const PERIOD_SLOTS = {
  '1st year': [
    { period: 1, start: '09:00', end: '10:00', label: '9:00 – 10:00' },
    { period: 2, start: '10:00', end: '11:00', label: '10:00 – 11:00' },
    { period: 3, start: '11:00', end: '12:00', label: '11:00 – 12:00' },
    { period: 'lunch', start: '12:00', end: '12:40', label: '12:00 – 12:40 (Lunch)' },
    { period: 4, start: '12:40', end: '13:40', label: '12:40 – 1:40' },
    { period: 5, start: '13:40', end: '14:40', label: '1:40 – 2:40' },
    { period: 6, start: '14:40', end: '15:40', label: '2:40 – 3:40' },
    { period: 7, start: '15:40', end: '16:40', label: '3:40 – 4:40' },
  ],
  'default': [
    { period: 1, start: '10:00', end: '11:00', label: '10:00 – 11:00' },
    { period: 2, start: '11:00', end: '12:00', label: '11:00 – 12:00' },
    { period: 3, start: '12:00', end: '13:00', label: '12:00 – 1:00' },
    { period: 'lunch', start: '13:00', end: '13:40', label: '1:00 – 1:40 (Lunch)' },
    { period: 4, start: '13:40', end: '14:40', label: '1:40 – 2:40' },
    { period: 5, start: '14:40', end: '15:40', label: '2:40 – 3:40' },
    { period: 6, start: '15:40', end: '16:40', label: '3:40 – 4:40' },
  ]
};

const getSlots = (year) => year === '1st year' ? PERIOD_SLOTS['1st year'] : PERIOD_SLOTS['default'];

const FacultyTimetable = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('timetable'); // timetable | role
  const [mySchedule, setMySchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Block edit popup state
  const [editingBlock, setEditingBlock] = useState(null); // { day, slot, existingData }
  const [blockForm, setBlockForm] = useState({ block_type: 'class', year: '2nd year', branch: 'CSE', section: 'A', subject: '', other_reason: '', room_no: '' });

  // Role tab state
  const [roleRequest, setRoleRequest] = useState(null);
  const [facultyProfile, setFacultyProfile] = useState(null); // actual DB role
  const [roleForm, setRoleForm] = useState({ role_type: 'coordinator', year: '2nd year', branch: 'CSE', section: 'A' });
  const [roleSubmitting, setRoleSubmitting] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
    if (!userData.email || (userData.role !== "faculty" && userData.role !== "admin")) {
      navigate("/");
      return;
    }
    setUser(userData);
  }, [navigate]);

  const fetchMySchedule = useCallback(async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/timetable/my-schedule/${encodeURIComponent(user.email)}`);
      const data = await res.json();
      setMySchedule(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user]);

  const fetchRoleRequest = useCallback(async () => {
    if (!user?.email) return;
    try {
      const res = await fetch(`${API_URL}/faculty/my-role-request/${encodeURIComponent(user.email)}`);
      const data = await res.json();
      setRoleRequest(data);
    } catch (e) { console.error(e); }
  }, [user]);

  const fetchFacultyProfile = useCallback(async () => {
    if (!user?.email) return;
    try {
      const res = await fetch(`${API_URL}/faculty/profile?email=${encodeURIComponent(user.email)}`);
      const data = await res.json();
      setFacultyProfile(data);
    } catch (e) { console.error(e); }
  }, [user]);

  useEffect(() => {
    if (user?.email) {
      fetchMySchedule();
      fetchRoleRequest();
      fetchFacultyProfile();
    }
  }, [user, fetchMySchedule, fetchRoleRequest, fetchFacultyProfile]);

  // -------  Timetable helpers -------
  const getBlockData = (day, slot) => {
    return mySchedule.find(b => b.day_of_week === day && b.start_time === slot.start) || null;
  };

  const openEdit = (day, slot) => {
    const existing = getBlockData(day, slot);
    setBlockForm(existing ? {
      block_type: existing.block_type || 'class',
      year: existing.year || '2nd year',
      branch: existing.branch || 'CSE',
      section: existing.section || 'A',
      subject: existing.subject || '',
      other_reason: existing.other_reason || '',
      room_no: existing.room_no || ''
    } : { block_type: 'class', year: '2nd year', branch: 'CSE', section: 'A', subject: '', other_reason: '', room_no: '' });
    setEditingBlock({ day, slot, existing });
  };

  const saveBlock = async () => {
    setSaving(true);
    try {
      const payload = {
        faculty_email: user.email,
        faculty_name: user.name,
        day_of_week: editingBlock.day,
        start_time: editingBlock.slot.start,
        end_time: editingBlock.slot.end,
        period_number: editingBlock.slot.period,
        block_type: blockForm.block_type,
        year: (blockForm.block_type === 'class' || blockForm.block_type === 'lab') ? blockForm.year : null,
        branch: (blockForm.block_type === 'class' || blockForm.block_type === 'lab') ? blockForm.branch : null,
        section: (blockForm.block_type === 'class' || blockForm.block_type === 'lab') ? blockForm.section : null,
        subject: (blockForm.block_type === 'class' || blockForm.block_type === 'lab') ? blockForm.subject : null,
        other_reason: blockForm.other_reason || null,
        room_no: blockForm.block_type === 'lab' ? blockForm.room_no : null,
        is_lab: blockForm.block_type === 'lab'
      };

      const res = await fetch(`${API_URL}/timetable/save-block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setEditingBlock(null);
        fetchMySchedule();
      } else {
        alert('Failed to save block');
      }
    } catch (e) { console.error(e); alert('Error saving block'); }
    finally { setSaving(false); }
  };

  const deleteBlock = async (blockId) => {
    try {
      await fetch(`${API_URL}/timetable/block/${blockId}`, { method: 'DELETE' });
      fetchMySchedule();
    } catch (e) { console.error(e); }
  };

  const getBlockColor = (blockType) => {
    if (blockType === 'class') return 'var(--accent-color)';
    if (blockType === 'lab') return '#8b5cf6';
    if (blockType === 'lunch') return '#f59e0b';
    return '#6b7280';
  };

  // Determine which slot set to use (mix of years possible in schedule)
  // Show a full 9-4:40 grid using all possible slots
  const ALL_SLOTS_1ST = [
    { period: 1, start: '09:00', end: '10:00', label: '9:00 – 10:00' },
    { period: 2, start: '10:00', end: '11:00', label: '10:00 – 11:00' },
    { period: 3, start: '11:00', end: '12:00', label: '11:00 – 12:00' },
    { period: 'lunch-1', start: '12:00', end: '12:40', label: '12:00 – 12:40' },
    { period: 4, start: '12:40', end: '13:00', label: '12:40 – 1:00' },
    { period: 5, start: '13:00', end: '13:40', label: '1:00 – 1:40' },
    { period: 6, start: '13:40', end: '14:40', label: '1:40 – 2:40' },
    { period: 7, start: '14:40', end: '15:40', label: '2:40 – 3:40' },
    { period: 8, start: '15:40', end: '16:40', label: '3:40 – 4:40' },
  ];

  const ALL_SLOTS_SENIOR = [
    { period: 1, start: '09:00', end: '10:00', label: '9:00 – 10:00' },
    { period: 2, start: '10:00', end: '11:00', label: '10:00 – 11:00' },
    { period: 3, start: '11:00', end: '12:00', label: '11:00 – 12:00' },
    { period: 4, start: '12:00', end: '13:00', label: '12:00 – 1:00' },
    { period: 'lunch', start: '13:00', end: '13:40', label: '1:00 – 1:40' },
    { period: 5, start: '13:40', end: '14:40', label: '1:40 – 2:40' },
    { period: 6, start: '14:40', end: '15:40', label: '2:40 – 3:40' },
    { period: 7, start: '15:40', end: '16:40', label: '3:40 – 4:40' },
  ];

  // Role helpers
  const submitRoleRequest = async () => {
    setRoleSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/faculty/declare-role`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ faculty_email: user.email, ...roleForm })
      });
      const data = await res.json();
      if (res.ok) {
        alert('Role request submitted! Waiting for admin approval.');
        fetchRoleRequest();
      } else {
        alert(data.error || 'Failed to submit role request');
      }
    } catch (e) { alert('Error submitting role request'); }
    finally { setRoleSubmitting(false); }
  };

  const cancelRoleRequest = async () => {
    try {
      await fetch(`${API_URL}/faculty/cancel-role-request/${encodeURIComponent(user.email)}`, { method: 'DELETE' });
      fetchRoleRequest();
    } catch (e) { alert('Failed to cancel'); }
  };

  const getRoleStatusBadge = (status) => {
    if (status === 'pending') return { color: '#f59e0b', label: '⏳ Pending Admin Approval' };
    if (status === 'approved') return { color: '#10b981', label: '✅ Approved' };
    if (status === 'rejected') return { color: '#ef4444', label: '❌ Rejected' };
    return null;
  };

  if (!user) return null;

  return (
    <div className="home-page-container">
      <div className="home-content-wrapper" style={{ maxWidth: '1300px', margin: '0 auto' }}>
        <header style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/faculty/home')} className="back-btn-modern" style={{ position: 'static', transform: 'none', flexShrink: 0 }}>
            <ChevronLeft size={20} /> Back
          </button>
          <div style={{ flex: 1, minWidth: '120px' }}>
            <h1 className="brand-title" style={{ fontSize: '2rem', margin: 0 }}>Timetable Manager</h1>
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Manage your class schedule &amp; declare your role.</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <button onClick={() => navigate('/faculty/home')} title="Home" style={{ width: 46, height: 46, borderRadius: '14px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0 }}><Home size={20} /></button>
            <button onClick={() => { localStorage.removeItem('vjconnect_user'); navigate('/'); }} title="Logout" style={{ width: 46, height: 46, borderRadius: '14px', border: 'none', background: '#ef4444', color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(239,68,68,0.4)', flexShrink: 0 }}><LogOut size={20} /></button>
          </div>
        </header>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          {[
            { id: 'timetable', label: '📅 My Timetable' },
            { id: 'role', label: '🎓 My Role & Class' }
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ padding: '0.8rem 2rem', borderRadius: '30px', border: '1px solid var(--glass-border)', background: activeTab === tab.id ? 'var(--accent-color)' : 'var(--glass-bg)', color: activeTab === tab.id ? 'white' : 'var(--text-main)', cursor: 'pointer', fontWeight: 'bold', transition: 'all 0.3s' }}
            >{tab.label}</button>
          ))}
        </div>

        {/* ==== TIMETABLE TAB ==== */}
        {activeTab === 'timetable' && (() => {
          const hasFirstYear = mySchedule.some(b => b.year === '1st year') || roleForm.year === '1st year' || roleRequest?.year === '1st year';
          const slots = hasFirstYear ? ALL_SLOTS_1ST : ALL_SLOTS_SENIOR;
          return (
            <>
            <div className="glass-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.3rem', margin: '0 0 0.5rem 0' }}>📅 This Semester Timetable</h2>
              <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.9rem' }}>
                Click any time block to assign a class, add lunch, mark free time, or write a custom note.
                <br />Your entries will automatically appear in the Admin's class view.
              </p>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>Loading your schedule...</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: '1000px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid var(--glass-border)', color: 'var(--text-muted)', minWidth: '130px' }}>
                        <Clock size={14} style={{ marginRight: 4 }} /> Time
                      </th>
                      {DAYS.map(day => (
                        <th key={day} style={{ padding: '1rem', textAlign: 'center', borderBottom: '1px solid var(--glass-border)', color: 'var(--accent-color)', fontWeight: 'bold' }}>
                          {day}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {slots.map(slot => (
                      <tr key={slot.start}>
                        <td style={{ padding: '0.5rem 1rem', borderBottom: '1px solid var(--glass-border)', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                          {slot.label}
                        </td>
                        {DAYS.map(day => {
                          const block = getBlockData(day, slot);
                          return (
                            <td key={day} style={{ padding: '0.4rem', borderBottom: '1px solid var(--glass-border)', verticalAlign: 'top' }}>
                              <div
                                onClick={() => openEdit(day, slot)}
                                style={{
                                  minHeight: '60px',
                                  borderRadius: '8px',
                                  padding: '0.5rem',
                                  cursor: 'pointer',
                                  background: block ? `${getBlockColor(block.block_type)}22` : 'var(--glass-bg)',
                                  border: block ? `1px solid ${getBlockColor(block.block_type)}55` : '1px dashed var(--glass-border)',
                                  transition: 'all 0.2s',
                                  position: 'relative'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = block ? `${getBlockColor(block.block_type)}33` : 'var(--glass-hover)'}
                                onMouseLeave={e => e.currentTarget.style.background = block ? `${getBlockColor(block.block_type)}22` : 'var(--glass-bg)'}
                              >
                                {block ? (
                                  <>
                                    <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: getBlockColor(block.block_type) }}>
                                      {block.block_type === 'class' ? (
                                        <>
                                          <div>📚 {block.subject || 'Class'}</div>
                                          <div style={{ opacity: 0.85, marginTop: '0.2rem', color: 'var(--text-secondary)', fontWeight: 'normal', fontSize: '0.7rem' }}>{[block.year, block.branch, block.section].filter(Boolean).join(' – ')}</div>
                                        </>
                                      ) : block.block_type === 'lab' ? (
                                        <>
                                          <div>🧪 {block.subject || 'Lab'}</div>
                                          <div style={{ opacity: 0.85, marginTop: '0.2rem', color: 'var(--text-secondary)', fontWeight: 'normal', fontSize: '0.7rem' }}>{[block.year, block.branch, block.section].filter(Boolean).join(' – ')}</div>
                                        </>
                                      ) : block.block_type === 'lunch' ? (
                                        '🍽️ Lunch Break'
                                      ) : (
                                        <>📝 {block.other_reason || 'Other'}</>
                                      )}
                                    </div>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); deleteBlock(block.id); }}
                                      style={{ position: 'absolute', top: '0.3rem', right: '0.3rem', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.1rem', opacity: 0.6 }}
                                    >
                                      <X size={12} />
                                    </button>
                                  </>
                                ) : (
                                  <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center', paddingTop: '0.5rem' }}>+ Add</div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
          );
        })()}

        {/* ==== ROLE TAB ==== */}
        {activeTab === 'role' && (
          <div style={{ maxWidth: '700px' }}>
            <div className="glass-card" style={{ padding: '2rem' }}>
              <h2 style={{ fontSize: '1.3rem', margin: '0 0 1.5rem 0' }}>🎓 Declare Your Class Role</h2>

              {/* === ACTIVE ROLE (from faculty table — source of truth) === */}
              {(facultyProfile?.is_coordinator || facultyProfile?.is_hod) && (
                <div style={{ marginBottom: '1.5rem', padding: '1rem 1.2rem', borderRadius: '12px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}>
                  <div style={{ color: '#10b981', fontWeight: 'bold', marginBottom: '0.3rem' }}>✅ Active Role (Admin Confirmed)</div>
                  {facultyProfile?.is_coordinator && (
                    <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                      📌 <strong>Class Coordinator</strong> — {facultyProfile.coordinator_year} · {facultyProfile.coordinator_branch} · Section {facultyProfile.coordinator_section}
                    </div>
                  )}
                  {facultyProfile?.is_hod && (
                    <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                      🏫 <strong>HOD</strong> — {facultyProfile.hod_branch} Department
                    </div>
                  )}
                </div>
              )}

              {/* Current request status (from role_requests) */}
              {roleRequest && roleRequest.status !== 'approved' && (
                <div style={{ marginBottom: '2rem', padding: '1rem', borderRadius: '12px', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--glass-border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 'bold' }}>Pending Request:</span>
                    <span style={{ color: getRoleStatusBadge(roleRequest.status)?.color, fontWeight: 'bold', fontSize: '0.9rem' }}>
                      {getRoleStatusBadge(roleRequest.status)?.label}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    Role: <strong>{roleRequest.role_type === 'coordinator' ? 'Class Coordinator' : 'HOD'}</strong>
                    {roleRequest.role_type === 'coordinator' && ` for ${roleRequest.year} – ${roleRequest.branch} – Section ${roleRequest.section}`}
                    {roleRequest.role_type === 'hod' && ` for ${roleRequest.branch} department`}
                  </div>
                  {roleRequest.status === 'pending' && (
                    <button onClick={cancelRoleRequest}
                      style={{ marginTop: '0.8rem', background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: 'none', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}>
                      Cancel Request
                    </button>
                  )}
                </div>
              )}

              {/* Show submit form only if no active role AND no pending request */}
              {!facultyProfile?.is_coordinator && !facultyProfile?.is_hod && (!roleRequest || roleRequest.status === 'rejected') && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                    <div>
                      <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Role Type</label>
                      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                        {[{ value: 'coordinator', label: '📌 Class Coordinator' }, { value: 'hod', label: '🏫 HOD' }].map(opt => (
                          <button key={opt.value} onClick={() => setRoleForm(f => ({ ...f, role_type: opt.value }))}
                            style={{ flex: 1, padding: '0.8rem', borderRadius: '8px', border: 'none', background: roleForm.role_type === opt.value ? 'var(--accent-color)' : 'rgba(255,255,255,0.05)', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Branch / Department</label>
                      <select value={roleForm.branch} onChange={e => {
                        const val = e.target.value;
                        setRoleForm(f => ({ ...f, branch: val, section: (val === "CSE-AIML&IOT" && f.section === "D") ? "IOT" : f.section }));
                      }}
                        className="modern-select" style={{ width: '100%', padding: '0.8rem', marginTop: '0.5rem' }}>
                        {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>

                    {roleForm.role_type === 'coordinator' && (
                      <>
                        <div>
                          <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Year</label>
                          <select value={roleForm.year} onChange={e => setRoleForm(f => ({ ...f, year: e.target.value }))}
                            className="modern-select" style={{ width: '100%', padding: '0.8rem', marginTop: '0.5rem' }}>
                            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Section</label>
                          <select value={roleForm.section} onChange={e => setRoleForm(f => ({ ...f, section: e.target.value }))}
                            className="modern-select" style={{ width: '100%', padding: '0.8rem', marginTop: '0.5rem' }}>
                            {(roleForm.branch === "CSE-AIML&IOT" ? ["A", "B", "C", "IOT"] : SECTIONS).map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      </>
                    )}

                    <div style={{ padding: '1rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', fontSize: '0.85rem', color: '#f59e0b' }}>
                      <AlertTriangle size={14} style={{ display: 'inline', marginRight: 6 }} />
                      This request will be reviewed by the Admin. You'll be notified once approved or rejected.
                    </div>

                    <button onClick={submitRoleRequest} disabled={roleSubmitting}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--accent-color)', color: 'white', border: 'none', padding: '1rem 2rem', borderRadius: '30px', cursor: roleSubmitting ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '1rem' }}>
                      <Send size={18} /> {roleSubmitting ? 'Submitting...' : 'Submit Role Request'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Block Edit Modal */}
      {editingBlock && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setEditingBlock(null)}>
          <div className="glass-card" onClick={e => e.stopPropagation()}
            style={{ padding: '2rem', maxWidth: '450px', width: '90%', position: 'relative' }}>
            <button onClick={() => setEditingBlock(null)} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
              <X size={20} />
            </button>
            <h3 style={{ margin: '0 0 0.3rem 0' }}>{editingBlock.day}</h3>
            <p style={{ color: 'var(--text-muted)', margin: '0 0 1.5rem 0', fontSize: '0.9rem' }}>{editingBlock.slot.label}</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              <div>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Block Type</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.5rem' }}>
                  {[
                    { value: 'class', label: '📚 Class' },
                    { value: 'lab', label: '🧪 Lab' },
                    { value: 'lunch', label: '🍽️ Lunch' },
                    { value: 'other', label: '📝 Other' }
                  ].map(opt => (
                    <button key={opt.value} onClick={() => setBlockForm(f => ({ ...f, block_type: opt.value }))}
                      style={{ padding: '0.6rem', borderRadius: '8px', border: '1px solid var(--glass-border)', background: blockForm.block_type === opt.value ? (opt.value === 'lab' ? '#8b5cf6' : 'var(--accent-color)') : 'var(--glass-bg)', color: blockForm.block_type === opt.value ? 'white' : 'var(--text-main)', cursor: 'pointer', fontWeight: blockForm.block_type === opt.value ? 'bold' : 'normal', transition: 'all 0.2s' }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {(blockForm.block_type === 'class' || blockForm.block_type === 'lab') && (
                <>
                  {blockForm.block_type === 'lab' && (
                    <div style={{ padding: '0.6rem 0.8rem', background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: '8px', fontSize: '0.82rem', color: '#a78bfa' }}>
                      🧪 <strong>Lab block</strong> — multiple faculty can share the same class slot. All will receive attendance requests.
                    </div>
                  )}
                  <div>
                    <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Year</label>
                    <select value={blockForm.year} onChange={e => setBlockForm(f => ({ ...f, year: e.target.value }))}
                      className="modern-select" style={{ width: '100%', padding: '0.7rem', marginTop: '0.4rem' }}>
                      {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.8rem' }}>
                    <div>
                      <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Branch</label>
                      <select value={blockForm.branch} onChange={e => {
                        const val = e.target.value;
                        setBlockForm(f => ({ ...f, branch: val, section: (val === "CSE-AIML&IOT" && f.section === "D") ? "IOT" : f.section }));
                      }}
                        className="modern-select" style={{ width: '100%', padding: '0.7rem', marginTop: '0.4rem' }}>
                        {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Section</label>
                      <select value={blockForm.section} onChange={e => setBlockForm(f => ({ ...f, section: e.target.value }))}
                        className="modern-select" style={{ width: '100%', padding: '0.7rem', marginTop: '0.4rem' }}>
                        {(blockForm.branch === "CSE-AIML&IOT" ? ["A", "B", "C", "IOT"] : SECTIONS).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Subject Name</label>
                    <input value={blockForm.subject} onChange={e => setBlockForm(f => ({ ...f, subject: e.target.value }))}
                      placeholder={blockForm.block_type === 'lab' ? 'e.g. OS Lab, DBMS Lab' : 'e.g. Operating Systems'}
                      className="search-input-modern"
                      style={{ width: '100%', padding: '0.7rem', marginTop: '0.4rem' }} />
                  </div>
                  {blockForm.block_type === 'lab' && (
                    <div>
                      <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Lab / Room Number</label>
                      <input value={blockForm.room_no} onChange={e => setBlockForm(f => ({ ...f, room_no: e.target.value }))}
                        placeholder="e.g. Lab 3, CSE Lab 1"
                        className="search-input-modern"
                        style={{ width: '100%', padding: '0.7rem', marginTop: '0.4rem' }} />
                    </div>
                  )}
                </>
              )}

              {blockForm.block_type === 'other' && (
                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Reason</label>
                  <input value={blockForm.other_reason} onChange={e => setBlockForm(f => ({ ...f, other_reason: e.target.value }))}
                    placeholder="e.g. Lab session, Meeting..."
                    className="search-input-modern"
                    style={{ width: '100%', padding: '0.7rem', marginTop: '0.4rem' }} />
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.8rem' }}>
                <button onClick={() => setEditingBlock(null)}
                  style={{ flex: 1, padding: '0.8rem', borderRadius: '8px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', cursor: 'pointer', fontWeight: '600' }}>
                  Cancel
                </button>
                <button onClick={saveBlock} disabled={saving}
                  style={{ flex: 2, padding: '0.8rem', borderRadius: '8px', border: 'none', background: 'var(--accent-color)', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <Save size={16} /> {saving ? 'Saving...' : 'Save Block'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacultyTimetable;
